import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, dashboards } from '../api/client';
import type { DashboardDoc } from './types';

const KEY = ['dashboards'];

export function isConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409;
}

export function useDashboardList() {
  return useQuery({ queryKey: KEY, queryFn: () => dashboards.list() });
}

export function useDashboard(id: string | null) {
  return useQuery({
    queryKey: [...KEY, id],
    queryFn: () => dashboards.get(id!),
    enabled: id !== null,
  });
}

export function useCreateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (doc: DashboardDoc) => dashboards.create(doc),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useSaveDashboard() {
  const qc = useQueryClient();
  return useMutation({
    // expectedUpdatedAt is the optimistic-concurrency token; the server
    // returns 409 (→ isConflict) when it is stale.
    mutationFn: ({ id, doc, expectedUpdatedAt }: { id: string; doc: DashboardDoc; expectedUpdatedAt: string }) =>
      dashboards.update(id, doc, expectedUpdatedAt),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: [...KEY, id] });
    },
  });
}

export function useDeleteDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dashboards.remove(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}
